import { ReactElement, ReactNode } from 'react'
import produce from 'immer'
import { Group, Object3DEventMap, Plane } from 'three'
import create from 'zustand'
import createContext from 'zustand/context'
import { combine } from 'zustand/middleware'

interface Props {
  children: ReactNode
}

interface SceneStoreInitialState {
  guidToColor: Record<string, string>
  manualGuidToColor: Record<string, string>
  overlayGuidToColor: Record<string, string>
  storeyToWallLinewidth: Record<string, number>
  loadedGltfModels: Record<string, Group<Object3DEventMap>>
  gltfClipPlanes: Plane[]
  clipPlaneIds: string[]
  cameraEvents: Set<{
    guid: string
    event: CameraEvents
  }>
}

interface ControlStoreType extends SceneStoreInitialState {
  setGuidToColorMapping: (guidToColor: Record<string, string>) => void
  clearGuidToColorMapping: () => void
  setManualGuidToColorMapping: (guidToColor: Record<string, string>) => void
  clearManualGuidToColorMapping: () => void
  setWallLinewidthForStorey: (storey: string, linewidth?: number) => void
  addLoadedGltfModel: (guid: string, gltfModel: Group<Object3DEventMap>) => void
  removeLoadedGltfModel: (guid: string) => void
  setGltfClipPlanes: (planes: Plane[]) => void
  setClipPlaneIds: (ids: string[]) => void
  addCameraEvent: (event: { guid: string; event: CameraEvents }) => void
  removeCameraEvent: (guid: string) => void
}

const initialState: SceneStoreInitialState = {
  guidToColor: {},
  manualGuidToColor: {},
  overlayGuidToColor: {},
  storeyToWallLinewidth: {},
  loadedGltfModels: {},
  gltfClipPlanes: [],
  clipPlaneIds: [],
  cameraEvents: new Set(),
}

const createStore = () =>
  create(
    combine(
      {
        ...initialState,
      },
      set => ({
        setGuidToColorMapping: (guidToColor: Record<string, string>) =>
          set({
            overlayGuidToColor: guidToColor,
            guidToColor,
          }),
        clearGuidToColorMapping: () =>
          set({
            overlayGuidToColor: {},
            guidToColor: {},
          }),
        setManualGuidToColorMapping: (guidToColor: Record<string, string>) =>
          set({
            manualGuidToColor: guidToColor,
          }),
        clearManualGuidToColorMapping: () =>
          set({
            manualGuidToColor: {},
          }),
        setWallLinewidthForStorey: (storey: string, linewidth?: number) =>
          set(
            produce(state => {
              if (linewidth === undefined) {
                delete state.storeyToWallLinewidth[storey]
              } else {
                state.storeyToWallLinewidth[storey] = linewidth
              }
            }),
          ),
        addLoadedGltfModel: (guid: string, gltfModel: Group<Object3DEventMap>) =>
          set(
            produce(state => {
              state.loadedGltfModels[guid] = gltfModel
            }),
          ),
        removeLoadedGltfModel: (guid: string) =>
          set(
            produce(state => {
              delete state.loadedGltfModels[guid]
            }),
          ),
        setGltfClipPlanes: (planes: Plane[]) => set({ gltfClipPlanes: planes }),
        setClipPlaneIds: (ids: string[]) => set({ clipPlaneIds: ids }),
        addCameraEvent: (event: { guid: string; event: CameraEvents }) =>
          set(
            produce(state => {
              state.cameraEvents.add(event)
            }),
          ),
        removeCameraEvent: (guid: string) =>
          set(
            produce(state => {
              for (const event of state.cameraEvents) {
                if (event.guid === guid) {
                  state.cameraEvents.delete(event)
                  break
                }
              }
            }),
          ),
      }),
    ),
  )

const { Provider, useStore: useSceneStore } = createContext<ControlStoreType>()

const SceneStoreProvider = ({ children }: Props): ReactElement => (
  <Provider createStore={createStore}>{children}</Provider>
)

export { SceneStoreProvider, useSceneStore }
